import { useCallback, useEffect, useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { listFindings, type Finding } from './api'
import { FINDING_KIND_LABEL, SEVERITY_CLASS, SEVERITY_ORDER } from './vocab'

/**
 * Every finding this module has produced, newest first.
 *
 * ## Sorted by severity within the page, not across the database
 *
 * The server returns newest-first because that is what an index can do cheaply;
 * the page then groups by severity, because "what is critical" is the question
 * somebody opens this screen with. Sorting the whole table by severity on the
 * server would mean a CRITICAL finding from March permanently outranking a HIGH
 * one from this morning.
 *
 * ## `soWhat` is the line in bold, not the title
 *
 * The title says what is true; the consequence says why anybody should care. The
 * database refuses a finding without one, and this list leads with it — a screen
 * of thirty titles with no consequences is a screen people scroll past.
 */
export function FindingsPage() {
  const [findings, setFindings] = useState<Finding[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [severity, setSeverity] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      setFindings(await listFindings(0, 100))
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const shown = useMemo(() => {
    const list = (findings ?? []).filter((f) => !severity || f.severity === severity)
    return [...list].sort(
      (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity))
  }, [findings, severity])

  const counts = useMemo(() => {
    const map = new Map<string, number>()
    ;(findings ?? []).forEach((f) => map.set(f.severity, (map.get(f.severity) ?? 0) + 1))
    return map
  }, [findings])

  return (
    <div className="space-y-3">
      <div>
        <h1 className="text-erp-lg font-semibold">Findings</h1>
        <p className="text-erp-xs text-muted-foreground mt-0.5">
          Across every report. A finding that a red team disputed is marked rather than removed —
          two models from two companies disagreeing is worth reading twice.
        </p>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />
      {findings === null && !error && <LoadingBlock />}

      {findings && (
        <div className="flex flex-wrap gap-1.5">
          <button type="button" onClick={() => setSeverity(null)}
            className={cn('erp-btn h-8 px-2.5 text-erp-xs',
              severity === null && 'border-chocolate text-chocolate')}>
            All ({findings.length})
          </button>
          {SEVERITY_ORDER.filter((s) => counts.has(s)).map((s) => (
            <button key={s} type="button" onClick={() => setSeverity(s)}
              className={cn('erp-btn h-8 px-2.5 text-erp-xs',
                severity === s && 'border-chocolate text-chocolate')}>
              {s} ({counts.get(s)})
            </button>
          ))}
        </div>
      )}

      {findings && shown.length === 0 && (
        <Note tone="info">Nothing at this severity.</Note>
      )}

      <ul className="space-y-2">
        {shown.map((finding) => (
          <li key={finding.id} className="rounded-md border border-border px-3 py-2.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className={cn('rounded border px-1.5 py-px text-erp-xs',
                SEVERITY_CLASS[finding.severity])}>
                {finding.severity}
              </span>
              <span className="text-erp-xs text-muted-foreground">
                {FINDING_KIND_LABEL[finding.kind] ?? finding.kind}
              </span>
              {finding.contested && (
                <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-px
                  text-erp-xs text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
                  disputed
                </span>
              )}
            </div>
            <div className="text-erp-sm font-medium mt-1">{finding.soWhat}</div>
            <div className="text-erp-sm text-muted-foreground mt-0.5">{finding.title}</div>
            <p className="text-erp-xs text-muted-foreground/80 mt-1">{finding.detail}</p>
            {finding.evidence.length > 0 && (
              <div className="mt-1 text-erp-xs text-muted-foreground/70">
                {finding.evidence.map((e, i) => (
                  <code key={i} className="mr-1.5 rounded bg-muted px-1 py-px">
                    {e.datasetId}
                  </code>
                ))}
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
